﻿import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { BIBlack, BIBlue, BIGrey, BILightGrey, lightenHexColor } from "../../assets/helpers";
import { Getrequestcall } from "../../request/Getrequest";
import {
  FETCH_PRODUCT_PROPERTIES,
  GET_PRODUCT_GROUPED_BY_MANUFACTUERE
} from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import { useSelector } from "react-redux";
import { English } from "../../constant/EnglishFallback";

const FilterModal = ({
  open,
  onClose,
  onApply = () => {},
  onReset = () => {},
  setSearchTerm,
  initialFilters = {},
  verifiedFilter,
  setVerifiedFilter,
  manufacturerId = null,
  color = ""
}) => {
  const { loginData, multiLanguageData } = useSelector((state) => state?.main);

  const LanguageData = multiLanguageData?.Manufacturers || {};

  const {
    TEXT_NEW_FILTERS = English.Manufacturers.TEXT_NEW_FILTERS,
    TEXT_SEARCH_FILTER = English.Manufacturers.TEXT_SEARCH_FILTER,
    TEXT_FILTER_PRODUCT_TYPE = English.Manufacturers.TEXT_FILTER_PRODUCT_TYPE,
    TEXT_VERIFIED = English.Manufacturers.TEXT_VERIFIED,
    TEXT_GROUP = English.Manufacturers.TEXT_GROUP,
    BUTTON_CLEAR_ALL = English.Manufacturers.BUTTON_CLEAR_ALL,
    BUTTON_APPLY = English.Manufacturers.BUTTON_APPLY
  } = LanguageData || {};

  const [selectedType, setSelectedType] = useState("All");
  const [isVerified, setIsVerified] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupChildren, setSelectedGroupChildren] = useState([]);
  const [groupFilterExpanded, setGroupFilterExpanded] = useState(true);

  // Data states
  const [manufactureGroupsData, setManufactureGroupsData] = useState([]);
  const [manufactureProperties, setManufactureProperties] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Fetch filter data when modal opens
  useEffect(() => {
    if (open && manufacturerId) {
      fetchFilterData();
    }
  }, [open, manufacturerId]);

  // Initialize filters when modal opens with previous filter state
  useEffect(() => {
    if (open) {
      setSelectedType(initialFilters.type || "All");
      setIsVerified(initialFilters.verified || verifiedFilter || false);
      setSelectedGroup(initialFilters.selectedGroup || null);
      setSelectedGroupChildren(initialFilters.selectedGroupChildren || []);

      // Initialize properties with previous selections
      if (initialFilters.properties && manufactureProperties.length > 0) {
        const updatedProperties = manufactureProperties.map((property) => ({
          ...property,
          options: property.options.map((option) => ({
            ...option,
            isSelected: initialFilters.properties.some(
              (p) => p.propertyId === property.id && p.propertyOptionId === option.id
            )
          }))
        }));
        setManufactureProperties(updatedProperties);
      }
    }
  }, [open, initialFilters, manufactureProperties.length, verifiedFilter]);

  const fetchFilterData = async () => {
    if (!manufacturerId) return;

    setIsLoading(true);
    try {
      // Fetch manufacturer groups
      const groupsResponse = await Getrequestcall(
        `${GET_PRODUCT_GROUPED_BY_MANUFACTUERE}/${manufacturerId}`
      );
      const groupsData = groupsResponse?.data?.data || [];

      // Fetch manufacturer properties
      const propertiesPayload = {
        SearchList: [
          {
            ManufacturerId: manufacturerId
          }
        ]
      };
      const propertiesResponse = await Postrequestcall(
        FETCH_PRODUCT_PROPERTIES,
        propertiesPayload,
        loginData?.accessToken
      );
      const propertiesData = addIsSelectedToOptions(propertiesResponse?.data?.data || []);

      setManufactureGroupsData(groupsData);
      setManufactureProperties(propertiesData);
    } catch (error) {
      console.log("ERROR: fetchFilterData >", error);
    } finally {
      setIsLoading(false);
    }
  };

  const addIsSelectedToOptions = (data) => {
    return data.map((property) => ({
      ...property,
      options: property.options.map((option) => ({
        ...option,
        isSelected: initialFilters.properties?.some(
          (p) => p.propertyId === property.id && p.propertyOptionId === option.id
        ) || false
      }))
    }));
  };

  const getSelectedOptionIds = () => {
    const selected = [];
    manufactureProperties.forEach((property) => {
      property.options.forEach((option) => {
        if (option.isSelected) {
          selected.push({
            propertyId: property.id,
            propertyOptionId: option.id
          });
        }
      });
    });
    return selected;
  };

  const handleTypePressed = (label) => {
    let filterTypeValue = "All";
    if (selectedType !== label) {
      filterTypeValue = label;
    }
    setSelectedType(filterTypeValue);
  };

  const handleGroupToggle = (group) => {
    const hasGroupAnyChild = group.children && group.children.length > 0;

    if (!hasGroupAnyChild) {
      if (selectedGroup === group.id) {
        setSelectedGroup(null);
      } else {
        setSelectedGroup(group.id);
        setSelectedGroupChildren([]);
      }
      return;
    }

    const allChildrenSelected = group.children.every((child) =>
      selectedGroupChildren.includes(child.id)
    );

    if (allChildrenSelected && selectedGroup === group.id) {
      setSelectedGroup(null);
      setSelectedGroupChildren([]);
    } else {
      setSelectedGroup(group.id);
      setSelectedGroupChildren(group.children.map((child) => child.id));
    }
  };

  const handleChildToggle = (child, group) => {
    const isChildSelected = selectedGroupChildren.includes(child.id);
    let updatedChildren = [...selectedGroupChildren];

    if (isChildSelected) {
      updatedChildren = updatedChildren.filter((id) => id !== child.id);
    } else {
      updatedChildren.push(child.id);
    }

    if (selectedGroup !== group.id) {
      setSelectedGroup(group.id);
      setSelectedGroupChildren([child.id]);
    } else {
      setSelectedGroupChildren(updatedChildren);
      if (updatedChildren.length === 0) {
        setSelectedGroup(null);
      }
    }
  };

  const toggleProperties = ({ propertyIndex, option }) => {
    const cloneData = [...manufactureProperties];
    const updated = cloneData.map((property, index) => {
      if (index === propertyIndex) {
        return {
          ...property,
          options: property.options.map((opt) => {
            if (opt.id === option.id) {
              return {
                ...opt,
                isSelected: !opt.isSelected
              };
            } else {
              return {
                ...opt,
                isSelected: false // Only one option can be selected per property
              };
            }
          })
        };
      }
      return property;
    });
    setManufactureProperties(updated);
  };

  const clearAllSelectedProperties = (propertyIndex) => {
    const updatedProperties = [...manufactureProperties];
    const propertyToUpdate = updatedProperties[propertyIndex];
    const updatedOptions = propertyToUpdate.options.map((option) => ({
      ...option,
      isSelected: false
    }));
    updatedProperties[propertyIndex] = {
      ...propertyToUpdate,
      options: updatedOptions
    };
    setManufactureProperties(updatedProperties);
  };

  const checkIfAnyPropertyIsSelected = (optionsArray = []) => {
    if (optionsArray && Array.isArray(optionsArray)) {
      return optionsArray.some((option) => option?.isSelected === true);
    }
    return false;
  };

  const countSelectedOptions = () => {
    let count = 0;
    manufactureProperties.forEach((property) => {
      property.options.forEach((option) => {
        if (option.isSelected) {
          count++;
        }
      });
    });
    return count;
  };

  const handleApplyClick = () => {
    const selectedProperties = getSelectedOptionIds();

    const filters = {
      type: selectedType,
      verified: isVerified,
      selectedGroup,
      selectedGroupChildren,
      properties: selectedProperties
    };

    // Pass all filter data to parent
    // onClose(true, filters);
    onApply(filters);
  };

  const handleResetClick = () => {
    setSelectedType("All");
    setIsVerified(false);
    setSelectedGroup(null);
    setSelectedGroupChildren([]);

    // Reset all properties
    const resetProperties = manufactureProperties.map((property) => ({
      ...property,
      options: property.options.map((option) => ({
        ...option,
        isSelected: false
      }))
    }));
    setManufactureProperties(resetProperties);

    onReset();
  };

  // Calculate filter count properly
  let filterCount = 0;
  if (selectedType && selectedType !== "All") filterCount++;
  if (isVerified) filterCount++;
  if (selectedGroup && selectedGroupChildren.length === 0) filterCount++;
  if (selectedGroupChildren && selectedGroupChildren.length > 0)
    filterCount += selectedGroupChildren.length;

  const selectedPropertiesCount = countSelectedOptions();
  filterCount += selectedPropertiesCount;

  return (
    <Modal open={open} onClose={() => onClose(false, null)}>
      <Box sx={{ position: "relative" }} className="user-modal-responsive">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              zIndex: 20
            }}>
            <CircularProgress />
          </Box>
        )}

        <Stack sx={{ position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "white", justifyContent: "space-between", alignItems: "center" }} direction="row">
          <Typography sx={{ fontWeight: 600 }} variant="h6">
            {filterCount > 0 ? TEXT_NEW_FILTERS : TEXT_SEARCH_FILTER}
            {filterCount > 0 && `(${filterCount})`}
          </Typography>
          <IconButton size="small" onClick={() => onClose(false, null)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack spacing={2} divider={<Divider />}>
          {/* Product Type Section */}
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 500 }}>{TEXT_FILTER_PRODUCT_TYPE}</Typography>
            <Stack sx={{ gap: 1,  justifyContent: "space-between", alignItems: "center"  }} direction="row" >
              <Stack sx={{ gap: 1 }} direction="row" >
                {["Tool", "Material"].map((type) => (
                  <FilterTypeButton
                    key={type}
                    type={type}
                    selectedType={selectedType}
                    onClick={() => handleTypePressed(type)}
                    color={color}
                  />
                ))}
              </Stack>
              {selectedType !== "All" && (
                <IconButton size="small" onClick={() => setSelectedType("All")}>
                  <CloseIcon sx={{ color: BIGrey }} />
                </IconButton>
              )}
            </Stack>
          </Stack>

          {/* Verified Section */}
          <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
            <Typography sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
              {TEXT_VERIFIED} <HelpIcon sx={{ height: 20, color: BIGrey }} />
            </Typography>
            <Checkbox
              checked={isVerified}
              onChange={(e) => setIsVerified(e.target.checked)}
              sx={{
                p: 0,
                "&.Mui-checked": {
                  color: color
                }
              }}
            />
          </Stack>

          {/* Groups Section */}
          {manufactureGroupsData.length > 0 && (
            <Stack spacing={1}>
              <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row" onClick={() => setGroupFilterExpanded(!groupFilterExpanded)}
                sx={{ cursor: "pointer" }}>
                <Typography sx={{ fontWeight: 500 }}>{TEXT_GROUP}</Typography>
                <IconButton size="small" sx={{ color: color }}>
                  {groupFilterExpanded ? "▲" : "▼"}
                </IconButton>
              </Stack>

              {groupFilterExpanded && (
                <Box sx={{ ml: 1 }}>
                  {manufactureGroupsData.map((group, index) => {
                    const hasGroupAnyChild = group.children && group.children.length > 0;
                    const allChildrenSelected =
                      hasGroupAnyChild &&
                      group.children.every((child) => selectedGroupChildren.includes(child.id));
                    const isGroupSelected = hasGroupAnyChild
                      ? allChildrenSelected && selectedGroup === group.id
                      : selectedGroup === group.id;

                    return (
                      <Box key={group.id}>
                        <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row" onClick={() => handleGroupToggle(group)}
                          sx={{ cursor: "pointer", py: 0.5 }}>
                          <Typography>{group.name}</Typography>
                          {isGroupSelected && <CheckIcon sx={{ color: color, height: 20 }} />}
                        </Stack>

                        {index !== manufactureGroupsData.length - 1 && <Divider />}

                        {/* Children */}
                        {hasGroupAnyChild && (
                          <Stack sx={{ ml: 2 }} spacing={0.5}>
                            {group.children.map((child) => {
                              const isChildSelected = selectedGroupChildren.includes(child.id);
                              return (
                                <Box key={child.id}>
                                  <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row" onClick={() => handleChildToggle(child, group)}
                                    sx={{ cursor: "pointer", py: 0.5 }}>
                                    <Typography variant="body2">{child.name}</Typography>
                                    {isChildSelected && selectedGroup === group.id && (
                                      <CheckIcon sx={{ color: color, height: 16 }} />
                                    )}
                                  </Stack>
                                  <Divider />
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Stack>
          )}

          {/* Properties Section */}
          {manufactureProperties.length > 0 &&
            manufactureProperties.map((property, propertyIndex) => (
              <Stack key={property.id} spacing={1}>
                <Typography sx={{ fontWeight: 500 }}>{property.name}</Typography>
                <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
                  <Stack sx={{ gap: 1 }} direction="row" >
                    {property.options.map((option) => (
                      <FilterTypeButton
                        key={option.id}
                        type={option.value}
                        selectedType={option.isSelected ? option.value : null}
                        onClick={() => toggleProperties({ propertyIndex, option })}
                      />
                    ))}
                  </Stack>
                  {checkIfAnyPropertyIsSelected(property.options) && (
                    <IconButton
                      size="small"
                      onClick={() => clearAllSelectedProperties(propertyIndex)}>
                      <CloseIcon sx={{ color: BIGrey }} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            ))}
        </Stack>

        {/* Footer Buttons */}
        <Stack sx={{ mt: 2,  position: "sticky",
            bottom: 0,
            zIndex: 10,
            backgroundColor: "white", justifyContent: "space-between" }} direction="row"  spacing={1}>
          <Button
            variant="text"
            onClick={handleResetClick}
            sx={{ textTransform: "none", color: "black" }}>
            {BUTTON_CLEAR_ALL}
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyClick}
            sx={{ textTransform: "none", background: color }}>
            {BUTTON_APPLY}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default FilterModal;

const FilterTypeButton = ({ type, selectedType, color, onClick = () => {} }) => (
  <Button
    onClick={() => onClick(type)}
    sx={{
      textTransform: "none",
      backgroundColor: selectedType === type ? lightenHexColor(color, 90) : "transparent",
      color: selectedType === type ? color : BIBlack,
      border: `1px solid ${selectedType === type ? "transparent" : BILightGrey}`,
      minWidth: "auto",
      px: 2
    }}>
    {type}
  </Button>
);

